import { useState, useEffect } from 'react';
import axios from 'axios';
import { formatDate, getTodayISO, isOverdue, getDaysOverdue, formatIndianCurrency } from '../utils/dateUtils';
import DateFilter, { filterByDateRange } from '../components/DateFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  'Draft': 'bg-gray-100 text-gray-800',
  'Pending': 'bg-yellow-100 text-yellow-800',
  'Approved': 'bg-green-100 text-green-800',
  'Material Sent': 'bg-blue-100 text-blue-800',
  'In Progress': 'bg-purple-100 text-purple-800',
  'Completed': 'bg-emerald-100 text-emerald-800',
  'Rejected': 'bg-red-100 text-red-800',
  'Cancelled': 'bg-gray-300 text-gray-700',
};

const jobWorkTypes = [
  'Threading', 'Hardening', 'Heat Treatment', 'Surface Grinding', 'Cylindrical Grinding', 'Polishing',
  'Plating', 'Powder Coating', 'Shot Blasting', 'Laser Cutting', 'Plasma Cutting', 'Forging', 'Blackening',
  'Painting', 'Welding', 'Machining', 'Assembly', 'Stamping', 'Other'
];

const unitOptions = ['pieces', 'kg', 'gm', 'meter', 'feet', 'inch', 'set', 'lot', 'nos', 'pair'];

export default function JobWorkPOs() {
  const [pos, setPOs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorItems, setVendorItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [poDetails, setPODetails] = useState(null);
  const [filterVendor, setFilterVendor] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterOverdue, setFilterOverdue] = useState(false);
  const [dateFilters, setDateFilters] = useState({ startDate: null, endDate: null });
  const [user, setUser] = useState(null);
  
  // Multi-item PO form state
  const [poForm, setPoForm] = useState({
    po_number: '',
    company_id: '',
    vendor_id: '',
    vendor_name: '',
    po_date: getTodayISO(),
    expected_completion_date: '',
    cgst_percent: 9,
    sgst_percent: 9,
    remarks: ''
  });
  
  // Items array for multi-item PO
  const [poItems, setPoItems] = useState([]);
  
  // Current item being added
  const [currentItem, setCurrentItem] = useState({
    partcode: '',
    description: '',
    item_type: 'Job Work',
    job_work_type: '',
    quantity: '',
    rate_per_piece: '',
    unit: 'pieces'
  });

  useEffect(() => {
    fetchData();
    fetchUser();
    fetchCompanies();
  }, [filterVendor, filterStatus]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
      // Set default company if not set
      if (response.data.length > 0 && !poForm.company_id) {
        const defaultCompany = response.data.find(c => c.is_default) || response.data[0];
        setPoForm(prev => ({ ...prev, company_id: defaultCompany.id }));
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [posRes, vendorsRes] = await Promise.all([
        axios.get(`${API}/jobwork/pos`, {
          params: { vendor_id: filterVendor || undefined, status: filterStatus || undefined }
        }),
        axios.get(`${API}/jobwork/vendors`)
      ]);
      setPOs(posRes.data);
      setVendors(vendorsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorItems = async (vendorId) => {
    try {
      const response = await axios.get(`${API}/jobwork/vendor-items/${vendorId}`);
      setVendorItems(response.data);
    } catch (error) {
      console.error('Error fetching vendor items:', error);
    }
  };

  const fetchNextPONumber = async (companyId) => {
    try {
      const response = await axios.get(`${API}/jobwork/po-number/next`, {
        params: { company_id: companyId }
      });
      return response.data.po_number;
    } catch (error) {
      console.error('Error fetching PO number:', error);
      return '';
    }
  };

  const handleCompanyChange = async (e) => {
    const companyId = e.target.value;
    setPoForm(prev => ({ ...prev, company_id: companyId }));
    
    // Fetch new PO number for selected company
    if (companyId) {
      const newPoNumber = await fetchNextPONumber(companyId);
      setPoForm(prev => ({ ...prev, company_id: companyId, po_number: newPoNumber }));
    }
  };

  const handleVendorChange = async (e) => {
    const vendorId = e.target.value;
    const vendor = vendors.find(v => v.id === vendorId);
    
    setPoForm({
      ...poForm,
      vendor_id: vendorId,
      vendor_name: vendor ? vendor.name : ''
    });
    
    if (vendorId) {
      await fetchVendorItems(vendorId);
    } else {
      setVendorItems([]);
    }
  };

  const handleItemFromCatalog = (e) => {
    const itemId = e.target.value;
    const item = vendorItems.find(i => i.id === itemId);
    
    if (item) {
      setCurrentItem({
        partcode: item.partcode,
        description: item.description,
        item_type: item.item_type,
        job_work_type: item.job_work_type || '',
        quantity: '',
        rate_per_piece: item.rate_per_piece || '',
        unit: item.unit || 'pieces'
      });
    }
  };

  const addItemToPO = () => {
    if (!currentItem.partcode || !currentItem.quantity || !currentItem.rate_per_piece) {
      alert('Please fill in all required item fields');
      return;
    }
    
    const newItem = {
      ...currentItem,
      quantity: parseInt(currentItem.quantity),
      rate_per_piece: parseFloat(currentItem.rate_per_piece),
      unit: currentItem.unit || 'pieces',
      amount: parseInt(currentItem.quantity) * parseFloat(currentItem.rate_per_piece)
    };
    
    setPoItems([...poItems, newItem]);
    setCurrentItem({
      partcode: '',
      description: '',
      item_type: 'Job Work',
      job_work_type: '',
      quantity: '',
      rate_per_piece: '',
      unit: 'pieces'
    });
  };

  const removeItem = (index) => {
    setPoItems(poItems.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const subtotal = poItems.reduce((sum, item) => sum + (item.quantity * item.rate_per_piece), 0);
    const cgst = (subtotal * poForm.cgst_percent) / 100;
    const sgst = (subtotal * poForm.sgst_percent) / 100;
    const total = subtotal + cgst + sgst;
    return { subtotal, cgst, sgst, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (poItems.length === 0) {
      alert('Please add at least one item to the PO');
      return;
    }
    
    if (!poForm.company_id) {
      alert('Please select a company');
      return;
    }
    
    try {
      const payload = {
        po_number: poForm.po_number || null,  // Can be auto-generated
        company_id: poForm.company_id,
        vendor_id: poForm.vendor_id,
        vendor_name: poForm.vendor_name,
        po_date: poForm.po_date,
        expected_completion_date: poForm.expected_completion_date || null,
        cgst_percent: parseFloat(poForm.cgst_percent) || 0,
        sgst_percent: parseFloat(poForm.sgst_percent) || 0,
        remarks: poForm.remarks || null,
        items: poItems.map(item => ({
          partcode: item.partcode,
          description: item.description,
          item_type: item.item_type,
          job_work_type: item.job_work_type || null,
          quantity: item.quantity,
          rate_per_piece: item.rate_per_piece,
          unit: item.unit || 'pieces',
          amount: item.quantity * item.rate_per_piece
        }))
      };
      
      await axios.post(`${API}/jobwork/pos`, payload);
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating PO:', error);
      alert('Error creating PO: ' + (error.response?.data?.detail || error.message));
    }
  };

  const viewPODetails = async (po) => {
    try {
      const response = await axios.get(`${API}/jobwork/pos/${po.id}`);
      setPODetails(response.data);
      setSelectedPO(po);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error fetching PO details:', error);
    }
  };

  const handleApprovePO = async (poId) => {
    try {
      await axios.put(`${API}/jobwork/pos/${poId}/approve`);
      alert('PO approved successfully!');
      fetchData();
      if (poDetails) {
        viewPODetails({ id: poId });
      }
    } catch (error) {
      console.error('Error approving PO:', error);
      alert('Error approving PO: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRejectPO = async (poId) => {
    const reason = prompt('Enter rejection reason:');
    if (reason) {
      try {
        await axios.put(`${API}/jobwork/pos/${poId}/reject?reason=${encodeURIComponent(reason)}`);
        alert('PO rejected');
        fetchData();
        if (poDetails) {
          viewPODetails({ id: poId });
        }
      } catch (error) {
        console.error('Error rejecting PO:', error);
        alert('Error rejecting PO: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleCancelPO = async (poId) => {
    const reason = prompt('Enter cancellation reason:');
    if (reason) {
      try {
        await axios.put(`${API}/jobwork/pos/${poId}/cancel?reason=${encodeURIComponent(reason)}`);
        alert('PO cancelled successfully');
        fetchData();
        setShowDetailsModal(false);
      } catch (error) {
        console.error('Error cancelling PO:', error);
        alert('Error cancelling PO: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleDuplicatePO = async (po) => {
    try {
      const nextPONumber = await fetchNextPONumber();
      
      // Get full PO details
      const response = await axios.get(`${API}/jobwork/pos/${po.id}`);
      const poData = response.data.po;
      
      // Pre-fill form with PO data
      setPoForm({
        po_number: nextPONumber,
        vendor_id: poData.vendor_id,
        vendor_name: poData.vendor_name,
        po_date: getTodayISO(),
        expected_completion_date: '',
        cgst_percent: poData.cgst_percent || 9,
        sgst_percent: poData.sgst_percent || 9,
        remarks: `Duplicated from ${poData.po_number}`
      });
      
      // Copy items
      if (poData.items) {
        setPoItems(poData.items.map(item => ({
          partcode: item.partcode,
          description: item.description,
          item_type: item.item_type,
          job_work_type: item.job_work_type || '',
          quantity: item.quantity,
          rate_per_piece: item.rate_per_piece,
          amount: item.amount
        })));
      }
      
      // Load vendor items
      if (poData.vendor_id) {
        await fetchVendorItems(poData.vendor_id);
      }
      
      setShowDetailsModal(false);
      setShowCreateModal(true);
    } catch (error) {
      console.error('Error duplicating PO:', error);
      alert('Error duplicating PO: ' + (error.response?.data?.detail || error.message));
    }
  };

  const downloadPDF = async (poId) => {
    try {
      const response = await axios.get(`${API}/jobwork/pos/${poId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PO_${poId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error downloading PDF: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this PO?')) {
      try {
        await axios.delete(`${API}/jobwork/pos/${id}`);
        fetchData();
        if (showDetailsModal) {
          setShowDetailsModal(false);
        }
      } catch (error) {
        console.error('Error deleting PO:', error);
        alert('Error deleting PO: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const resetForm = () => {
    setPoForm({
      po_number: '',
      vendor_id: '',
      vendor_name: '',
      po_date: getTodayISO(),
      expected_completion_date: '',
      cgst_percent: 9,
      sgst_percent: 9,
      remarks: ''
    });
    setPoItems([]);
    setCurrentItem({
      partcode: '',
      description: '',
      item_type: 'Job Work',
      job_work_type: '',
      quantity: '',
      rate_per_piece: '',
      unit: 'pieces'
    });
    setVendorItems([]);
  };

  const openCreateModal = async () => {
    // Get default company
    const defaultCompany = companies.find(c => c.is_default) || companies[0];
    const companyId = defaultCompany?.id || '';
    
    const nextPONumber = companyId ? await fetchNextPONumber(companyId) : '';
    setPoForm({ 
      ...poForm, 
      po_number: nextPONumber,
      company_id: companyId,
      po_date: getTodayISO()
    });
    setShowCreateModal(true);
  };

  // Filter POs
  // Apply date filter first, then other filters
  const dateFilteredPOs = filterByDateRange(pos, 'po_date', dateFilters.startDate, dateFilters.endDate);
  
  const filteredPOs = dateFilteredPOs.filter(po => {
    if (filterOverdue) {
      return isOverdue(po.expected_completion_date, po.status);
    }
    return true;
  });

  // Count overdue POs
  const overdueCount = pos.filter(po => isOverdue(po.expected_completion_date, po.status)).length;

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  const totals = calculateTotals();

  return (
    <div className="space-y-6" data-testid="jobwork-pos">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Job Work Purchase Orders</h1>
          {overdueCount > 0 && (
            <p className="text-red-600 font-medium mt-1">
              ⚠️ {overdueCount} PO(s) overdue
            </p>
          )}
        </div>
        <button
          onClick={openCreateModal}
          className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition"
          data-testid="create-po-button"
        >
          + Create PO
        </button>
      </div>

      {/* Date Filter */}
      <DateFilter onFilterChange={setDateFilters} />

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Vendor</label>
            <select
              value={filterVendor}
              onChange={(e) => setFilterVendor(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All Vendors</option>
              {vendors.map(vendor => (
                <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All Status</option>
              <option value="Draft">Draft</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Material Sent">Material Sent</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Rejected">Rejected</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filterOverdue}
                onChange={(e) => setFilterOverdue(e.target.checked)}
                className="w-5 h-5 text-red-600 rounded"
              />
              <span className="text-sm font-medium text-red-600">Show Overdue Only ({overdueCount})</span>
            </label>
          </div>
        </div>
      </div>

      {/* POs Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expected</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPOs.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    {filterOverdue ? 'No overdue POs.' : 'No POs created yet. Click "Create PO" to add one.'}
                  </td>
                </tr>
              ) : (
                filteredPOs.map((po) => {
                  const poIsOverdue = isOverdue(po.expected_completion_date, po.status);
                  const daysOverdue = getDaysOverdue(po.expected_completion_date);
                  
                  return (
                    <tr 
                      key={po.id} 
                      className={`hover:bg-gray-50 ${poIsOverdue ? 'bg-red-50 border-l-4 border-red-500' : ''}`}
                    >
                      <td className="px-6 py-4 text-sm font-medium text-blue-600 cursor-pointer hover:underline"
                          onClick={() => viewPODetails(po)}>
                        {po.po_number}
                        {poIsOverdue && (
                          <span className="ml-2 px-2 py-0.5 text-xs bg-red-600 text-white rounded-full">
                            OVERDUE
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{po.vendor_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {po.items?.length || 0} item(s)
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                        Rs.{po.total_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{formatDate(po.po_date)}</td>
                      <td className={`px-6 py-4 text-sm ${poIsOverdue ? 'text-red-600 font-semibold' : 'text-gray-700'}`}>
                        {po.expected_completion_date ? (
                          <>
                            {formatDate(po.expected_completion_date)}
                            {poIsOverdue && (
                              <span className="block text-xs text-red-500">
                                ({daysOverdue} days overdue)
                              </span>
                            )}
                          </>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${statusColors[po.status] || statusColors['Draft']}`}>
                          {po.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm space-x-2">
                        <button
                          onClick={() => viewPODetails(po)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          View
                        </button>
                        {po.status !== 'Rejected' && po.approval_status !== 'Rejected' && po.status !== 'Cancelled' && (
                          <button
                            onClick={() => downloadPDF(po.id)}
                            className="text-green-600 hover:text-green-800"
                          >
                            PDF
                          </button>
                        )}
                        {user?.role === 'admin' && (
                          <button
                            onClick={() => handleDelete(po.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create PO Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Create Job Work PO</h2>
                <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Company & PO Header */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
                  <select
                    value={poForm.company_id}
                    onChange={handleCompanyChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    <option value="">Select Company</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name} ({company.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PO Number *</label>
                  <input
                    type="text"
                    value={poForm.po_number}
                    onChange={(e) => setPoForm({ ...poForm, po_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Auto-generated or enter custom"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave blank to auto-generate</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PO Date *</label>
                  <input
                    type="date"
                    value={poForm.po_date}
                    onChange={(e) => setPoForm({ ...poForm, po_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expected Completion</label>
                  <input
                    type="date"
                    value={poForm.expected_completion_date}
                    onChange={(e) => setPoForm({ ...poForm, expected_completion_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Vendor *</label>
                  <select
                    value={poForm.vendor_id}
                    onChange={handleVendorChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    <option value="">Select Vendor</option>
                    {vendors.map(vendor => (
                      <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Tax Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CGST %</label>
                  <input
                    type="number"
                    step="0.01"
                    value={poForm.cgst_percent}
                    onChange={(e) => setPoForm({ ...poForm, cgst_percent: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SGST %</label>
                  <input
                    type="number"
                    step="0.01"
                    value={poForm.sgst_percent}
                    onChange={(e) => setPoForm({ ...poForm, sgst_percent: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              {/* Add Items Section */}
              {poForm.vendor_id && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h3 className="font-semibold text-gray-900 mb-4">Add Items to PO</h3>
                  
                  {/* Item from Catalog */}
                  {vendorItems.length > 0 && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Select from Catalog (Optional)</label>
                      <select
                        onChange={handleItemFromCatalog}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="">-- Select to auto-fill --</option>
                        {vendorItems.map(item => (
                          <option key={item.id} value={item.id}>
                            {item.partcode} - {item.description} ({item.item_type})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Part Code *</label>
                      <input
                        type="text"
                        value={currentItem.partcode}
                        onChange={(e) => setCurrentItem({ ...currentItem, partcode: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Enter part code"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                      <input
                        type="text"
                        value={currentItem.description}
                        onChange={(e) => setCurrentItem({ ...currentItem, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Enter description"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                      <select
                        value={currentItem.item_type}
                        onChange={(e) => setCurrentItem({ ...currentItem, item_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="Job Work">Job Work</option>
                        <option value="Direct Supply">Direct Supply</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Job Work Type</label>
                      <input
                        type="text"
                        value={currentItem.job_work_type}
                        onChange={(e) => setCurrentItem({ ...currentItem, job_work_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="e.g., Threading"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                      <input
                        type="number"
                        value={currentItem.quantity}
                        onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Enter quantity"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Rate per Piece (Rs.) *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={currentItem.rate_per_piece}
                        onChange={(e) => setCurrentItem({ ...currentItem, rate_per_piece: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Enter rate"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                      <select
                        value={currentItem.unit}
                        onChange={(e) => setCurrentItem({ ...currentItem, unit: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        {unitOptions.map(unit => (
                          <option key={unit} value={unit}>{unit}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={addItemToPO}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                      >
                        + Add Item
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Items List */}
              {poItems.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Part Code</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Unit</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rate (Rs.)</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount (Rs.)</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {poItems.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2 text-sm">{index + 1}</td>
                          <td className="px-4 py-2 text-sm font-medium">{item.partcode}</td>
                          <td className="px-4 py-2 text-sm">{item.description}</td>
                          <td className="px-4 py-2 text-sm">{item.item_type}</td>
                          <td className="px-4 py-2 text-sm text-right">{item.quantity}</td>
                          <td className="px-4 py-2 text-sm text-center">{item.unit || 'pieces'}</td>
                          <td className="px-4 py-2 text-sm text-right">{formatIndianCurrency(item.rate_per_piece)}</td>
                          <td className="px-4 py-2 text-sm text-right font-semibold">
                            {(item.quantity * item.rate_per_piece).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-2 text-center">
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {/* Totals */}
                  <div className="bg-gray-50 p-4">
                    <div className="flex justify-end">
                      <div className="w-72 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Subtotal:</span>
                          <span>Rs.{totals.subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>CGST ({poForm.cgst_percent}%):</span>
                          <span>Rs.{totals.cgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>SGST ({poForm.sgst_percent}%):</span>
                          <span>Rs.{totals.sgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between font-bold text-lg border-t pt-2">
                          <span>Grand Total:</span>
                          <span className="text-red-600">Rs.{totals.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Remarks */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  value={poForm.remarks}
                  onChange={(e) => setPoForm({ ...poForm, remarks: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="2"
                  placeholder="Any additional remarks..."
                />
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={poItems.length === 0}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create PO
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PO Details Modal */}
      {showDetailsModal && poDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    PO Details: {poDetails.po.po_number}
                    {isOverdue(poDetails.po.expected_completion_date, poDetails.po.status) && (
                      <span className="ml-3 px-3 py-1 text-sm bg-red-600 text-white rounded-full">
                        ⚠️ OVERDUE by {getDaysOverdue(poDetails.po.expected_completion_date)} days
                      </span>
                    )}
                  </h2>
                  <span className={`px-3 py-1 rounded-full text-sm ${statusColors[poDetails.po.status] || statusColors['Draft']}`}>
                    {poDetails.po.status}
                  </span>
                </div>
                <button onClick={() => setShowDetailsModal(false)} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* PO Info */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Vendor</p>
                  <p className="font-semibold text-gray-900">{poDetails.po.vendor_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">PO Date</p>
                  <p className="font-semibold text-gray-900">{formatDate(poDetails.po.po_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Expected Completion</p>
                  <p className={`font-semibold ${isOverdue(poDetails.po.expected_completion_date, poDetails.po.status) ? 'text-red-600' : 'text-gray-900'}`}>
                    {poDetails.po.expected_completion_date ? formatDate(poDetails.po.expected_completion_date) : 'Not set'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Approval Status</p>
                  <p className="font-semibold text-gray-900">{poDetails.po.approval_status}</p>
                  {poDetails.po.approved_by && (
                    <p className="text-xs text-gray-500">by {poDetails.po.approved_by}</p>
                  )}
                </div>
              </div>

              {/* Admin Actions */}
              {user?.role === 'admin' && poDetails.po.approval_status === 'Pending' && (
                <div className="flex gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <span className="text-yellow-800 font-medium">Admin Action Required:</span>
                  <button
                    onClick={() => handleApprovePO(poDetails.po.id)}
                    className="px-4 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition"
                  >
                    ✓ Approve PO
                  </button>
                  <button
                    onClick={() => handleRejectPO(poDetails.po.id)}
                    className="px-4 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition"
                  >
                    ✗ Reject PO
                  </button>
                </div>
              )}

              {/* Items Table */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">PO Items</h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Part Code</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Sent</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Received</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Pending</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rate (Rs.)</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount (Rs.)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {poDetails.items_status.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2 text-sm">{index + 1}</td>
                          <td className="px-4 py-2 text-sm font-medium">{item.partcode}</td>
                          <td className="px-4 py-2 text-sm">{item.description}</td>
                          <td className="px-4 py-2 text-sm">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              item.item_type === 'Job Work' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {item.item_type}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-right">{item.quantity}</td>
                          <td className="px-4 py-2 text-sm text-right text-yellow-600">{item.sent}</td>
                          <td className="px-4 py-2 text-sm text-right text-green-600">{item.received}</td>
                          <td className="px-4 py-2 text-sm text-right text-red-600">{item.pending}</td>
                          <td className="px-4 py-2 text-sm text-right">{formatIndianCurrency(item.rate_per_piece)}</td>
                          <td className="px-4 py-2 text-sm text-right font-semibold">
                            {item.amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals Summary */}
              <div className="flex justify-end">
                <div className="w-80 bg-gray-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal:</span>
                    <span>Rs.{poDetails.po.subtotal?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>CGST ({poDetails.po.cgst_percent}%):</span>
                    <span>Rs.{poDetails.po.cgst_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>SGST ({poDetails.po.sgst_percent}%):</span>
                    <span>Rs.{poDetails.po.sgst_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Grand Total:</span>
                    <span className="text-red-600">Rs.{poDetails.po.total_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>

              {/* Material Sent History */}
              {poDetails.material_sent_records && poDetails.material_sent_records.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Material Sent History</h3>
                  <div className="bg-gray-50 rounded-lg overflow-hidden">
                    <table className="min-w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Part Code</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Quantity</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Challan</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {poDetails.material_sent_records.map((record, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-2 text-sm">{formatDate(record.sent_date)}</td>
                            <td className="px-4 py-2 text-sm">{record.partcode}</td>
                            <td className="px-4 py-2 text-sm font-semibold">{record.quantity_sent}</td>
                            <td className="px-4 py-2 text-sm">{record.challan_number || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Material Received History */}
              {poDetails.material_received_records && poDetails.material_received_records.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Material Received History</h3>
                  <div className="bg-gray-50 rounded-lg overflow-hidden">
                    <table className="min-w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Part Code</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Quantity</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Challan</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {poDetails.material_received_records.map((record, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-2 text-sm">{formatDate(record.received_date)}</td>
                            <td className="px-4 py-2 text-sm">{record.partcode}</td>
                            <td className="px-4 py-2 text-sm font-semibold">{record.quantity_received}</td>
                            <td className="px-4 py-2 text-sm">{record.challan_number || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Remarks */}
              {poDetails.po.remarks && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Remarks</h3>
                  <p className="text-gray-700">{poDetails.po.remarks}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between pt-4 border-t">
                <div className="space-x-2">
                  {poDetails.po.status !== 'Rejected' && poDetails.po.approval_status !== 'Rejected' && poDetails.po.status !== 'Cancelled' ? (
                    <button
                      onClick={() => downloadPDF(poDetails.po.id)}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                    >
                      📥 Download PDF
                    </button>
                  ) : (
                    <span className="px-6 py-2 bg-gray-300 text-gray-600 rounded-lg cursor-not-allowed inline-block">
                      📥 PDF Not Available
                    </span>
                  )}
                  <button
                    onClick={() => handleDuplicatePO(poDetails.po)}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    📋 Duplicate PO
                  </button>
                  {poDetails.po.status !== 'Completed' && poDetails.po.status !== 'Cancelled' && user?.role === 'admin' && (
                    <button
                      onClick={() => handleCancelPO(poDetails.po.id)}
                      className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
                    >
                      ❌ Cancel PO
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
